import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { DEFAULT_ROLE_PERMISSIONS, PERMISSION_TIERS, type Permission } from '@wms/shared';

/**
 * 【用户设计 2026-08-17】创建账号的分层权限选择器：
 *  - 三层分组（普通/管理/顶尖），每项一个开关式按钮；
 *  - 预设按钮（管理人员/普通人员）一键点亮对应层级全部权限，不锁定，可再手动增删；
 *  - 顶尖层默认灰锁，只有持 user.restore（顶尖标志）的操作者能看到"解锁"
 *    按钮，密码重验通过后恢复可点。前端变灰只是引导——真正的防提权
 *    （只能授出自己持有的权限、碰不到更强的账号）在 users.service 强制。
 */

export type PresetKey = 'MANAGER' | 'EMPLOYEE';

/**
 * 权限码 → 三语人话（用户反馈："改成中文能看懂的"）。i18next 把 . 当嵌套
 * 分隔符，码里的点替换成下划线做键；词条缺失时回退显示原码，永不空白。
 */
export function permissionLabel(t: TFunction, code: string): string {
  return t(`permissionNames.${code.replace(/\./g, '_')}`, code);
}

/** 预设 → 选中集（管理人员天然包含普通人员的全部权限） */
export function presetSelection(preset: PresetKey): Set<Permission> {
  return new Set(DEFAULT_ROLE_PERMISSIONS[preset]);
}

/**
 * 选中集 vs 预设角色默认 → 提交差量：
 * grants = 选了但角色默认没有；denies = 角色默认有但被关掉。
 */
export function diffAgainstRole(
  selected: ReadonlySet<Permission>,
  rolePreset: PresetKey,
): { grants: Permission[]; denies: Permission[] } {
  const defaults = new Set(DEFAULT_ROLE_PERMISSIONS[rolePreset]);
  return {
    grants: [...selected].filter((p) => !defaults.has(p)).sort(),
    denies: [...defaults].filter((p) => !selected.has(p)).sort(),
  };
}

function TierSection({
  titleKey,
  permissions,
  selected,
  onToggle,
  locked,
}: {
  readonly titleKey?: string;
  readonly permissions: readonly Permission[];
  readonly selected: ReadonlySet<Permission>;
  readonly onToggle: (p: Permission) => void;
  readonly locked?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      {titleKey && (
        <p className="field-hint" style={{ marginBottom: 'var(--space-1)' }}>
          {t(titleKey)}
        </p>
      )}
      <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-1)' }}>
        {permissions.map((p) => (
          <button
            key={p}
            type="button"
            className={selected.has(p) ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            aria-pressed={selected.has(p)}
            disabled={locked}
            style={locked ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            onClick={() => onToggle(p)}
            title={p}
          >
            {permissionLabel(t, p)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PermissionPicker({
  selected,
  onToggle,
  topUnlocked,
  canUnlockTop,
  onRequestUnlock,
  onApplyPreset,
  activePreset,
}: {
  readonly selected: ReadonlySet<Permission>;
  readonly onToggle: (p: Permission) => void;
  readonly topUnlocked: boolean;
  /** 只有顶尖操作者（持 user.restore）能看到解锁入口 */
  readonly canUnlockTop: boolean;
  readonly onRequestUnlock: () => void;
  readonly onApplyPreset: (preset: PresetKey) => void;
  readonly activePreset: PresetKey;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <div className="row" style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        <button
          type="button"
          className={activePreset === 'EMPLOYEE' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() => onApplyPreset('EMPLOYEE')}
        >
          {t('users.presetEmployee')}
        </button>
        <button
          type="button"
          className={activePreset === 'MANAGER' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() => onApplyPreset('MANAGER')}
        >
          {t('users.presetManager')}
        </button>
      </div>

      <TierSection
        titleKey="users.tierBasic"
        permissions={PERMISSION_TIERS.basic}
        selected={selected}
        onToggle={onToggle}
      />
      <TierSection
        titleKey="users.tierManager"
        permissions={PERMISSION_TIERS.manager}
        selected={selected}
        onToggle={onToggle}
      />
      <div className="row row--between" style={{ alignItems: 'center' }}>
        <p className="field-hint" style={{ margin: 0 }}>
          {t('users.tierTop')}
        </p>
        {canUnlockTop && !topUnlocked && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onRequestUnlock}>
            🔒 {t('users.unlockTop')}
          </button>
        )}
      </div>
      {!topUnlocked && <p className="field-hint">{t('users.topLockedHint')}</p>}
      <TierSection
        permissions={PERMISSION_TIERS.top}
        selected={selected}
        onToggle={onToggle}
        locked={!topUnlocked}
      />
    </div>
  );
}
